import { ESPLoader, Transport } from 'esptool-js';

// esptool-js(webserial.js Transport.readLoop())は、想定内のReadタイムアウトであっても
// 内部で直接console.error("Error reading from serial port:", ...)を呼んでいる。この呼び出し元
// (identifyIotBoard/readChipIdViaRom)ではtry/catchで正しく処理できているにもかかわらず、
// Next.jsの開発オーバーレイがこのconsole.errorを拾って「Console Error」として表示してしまう。
// ライブラリ内部のコードは直接修正できないため、このメッセージだけをピンポイントで抑制する。
// (モジュール読み込み時に1回だけ適用。HMRでの二重パッチを避けるためフラグでガードする。)
if (
    typeof window !== 'undefined' &&
    typeof console !== 'undefined' &&
    !(console.error as { __schoomyPatched?: boolean }).__schoomyPatched
) {
    const originalConsoleError = console.error.bind(console);
    const patchedConsoleError = (...args: unknown[]) => {
        if (typeof args[0] === 'string' && args[0].startsWith('Error reading from serial port:')) {
            return;
        }
        originalConsoleError(...args);
    };
    (patchedConsoleError as { __schoomyPatched?: boolean }).__schoomyPatched = true;
    console.error = patchedConsoleError;
}

// ROMブートローダーは工場出荷時からチップに焼き込まれている不変領域なので、
// ブロックエディターで一度も書き込んでいない・古いファームウェアのIoT系ボード(ブルー/将来のパープル等)でも検出できる。
//
// 【2026-07-21、一度3000ms/デフォルト7回リトライに変更したが実運用不可でrevert】
// 「オレンジ選択なのに実機がブルー」の誤判定(false negative)を減らす狙いで
// readChipIdViaRom()と同じ値に揃えたが、本物のオレンジ実機ではESP32からの応答が絶対に
// 来ないため、毎回7回の試行をフルに使い切ることになり、実機で「接続まで30秒近くかかる」
// という致命的な回帰を起こした。オレンジ選択は最も頻度の高い操作であり、そちらを毎回
// 犠牲にするのは実運用上ありえないため400ms/attempts=1に戻す。ブルー実機との誤判定
// (取りこぼし)は残存する既知の制約として受け入れる。ちゃんと直すなら「判定中」ローディング
// 表示+実測ログでの最小タイムアウト特定など、体感速度を犠牲にしない別アプローチが必要。
const PROBE_TIMEOUT_MS = 400;

// esptool-jsのTransport.disconnect()実装は、内部でreader.cancel()は呼ぶが
// releaseLock()を呼ばない(コンパイル済みJSで確認済み)。Web Streams APIの仕様上、
// cancel()だけではストリームのロックは解放されないため、disconnect()がport.close()に
// 到達する前提としているロック解放が、タイミング次第で間に合わない/失敗することがある。
// これが、別のボードへの切り替え時に間欠的にROM接続が失敗する不具合の実体だと考えられる
// (transport.disconnect()自体は.catch()で握りつぶされていたため、この失敗が見えていなかった)。
// disconnect()に任せきりにせず、ここでreader/writerのロックを明示的に解放してから
// port.close()する。
async function forceCloseTransport(port: SerialPort, transport: Transport): Promise<void> {
    try {
        await transport.disconnect();
    } catch (error) {
        // disconnect()自体が失敗した場合、esptool-js内部が保持しているreaderの参照へ
        // 直接アクセスしてロックを解放する(TypeScript上はprivateだが実行時は通常の
        // プロパティなので到達可能。esptool-jsのバージョンアップ時は要再確認)。
        // ここは今回の不具合の核心箇所だったため、握りつぶさずログに残す。
        console.warn('[useBoardWebSerialApiIdentify] transport.disconnect() failed, forcing lock release', error);
    }
    try {
        const internalReader = (transport as unknown as { reader?: ReadableStreamDefaultReader }).reader;
        internalReader?.releaseLock();
    } catch (error) {
        // 既に解放済み/無効な参照の場合はここに来る。想定内だが念のため残す。
        console.warn('[useBoardWebSerialApiIdentify] releaseLock() failed (likely already released)', error);
    }
    if (port.readable?.locked || port.writable?.locked) {
        // それでもロックが残っている場合、close()は例外を投げる可能性が高いが、
        // これ以上こちらから強制解除する手段は無い。最後の望みとして一応試す。
        console.warn('[useBoardWebSerialApiIdentify] port still locked after cleanup, forcing close()');
        await port.close().catch((error) => {
            console.warn('[useBoardWebSerialApiIdentify] forced port.close() also failed', error);
        });
    }
}

export interface IotBoardIdentity {
    // esptool-jsのチップターゲット名(例: "ESP32"=ブルー, "ESP32-S3"=将来のパープル候補)。
    // esptool-jsは既にesp32s3.jsを同梱しているため、パープル追加時もこの関数側の変更は不要な想定。
    chipName: string;
    // efuse由来の生MACアドレス("AA:BB:CC:DD:EE:FF"形式)。
    // 注意: 出荷用スケッチが送っているchip_id(scmUtils.getChipId(ESP.getEfuseMac())の戻り値、DB(m_boards.chip_id)に
    // 保存されている整数値)とこのMAC文字列が同一のものかは未確認。SchooMyUtilities.getChipId()の変換処理の
    // 実装がこのリポジトリ内に無く参照できないため、将来API連携する際は変換ロジックの一致を別途確認すること。
    macAddress: string;
}

// esploader.DEFAULT_TIMEOUTはコンストラクタのoptions経由では設定できず、
// インスタンスプロパティへの直接代入のみ対応(esptool-js内部実装依存。バージョンアップ時要再確認)。
export async function identifyIotBoard(port: SerialPort): Promise<IotBoardIdentity | null> {
    await port.close().catch(() => {});
    const transport = new Transport(port, true);
    const esploader = new ESPLoader({
        transport,
        baudrate: 115200,
        romBaudrate: 115200,
        terminal: {
            clean() {},
            writeLine() {},
            write() {},
        },
    });
    esploader.DEFAULT_TIMEOUT = PROBE_TIMEOUT_MS;
    let connected = false;
    try {
        // attempts=1に固定(detectChip()のデフォルト7回リトライによる遅延増大を避ける。
        // 上のPROBE_TIMEOUT_MSのコメント参照)。
        await esploader.connect('default_reset', 1, true);
        connected = true;
        if (!esploader.chip) return null;
        const macAddress = await esploader.chip.readMac(esploader);
        return { chipName: esploader.chip.CHIP_NAME, macAddress };
    } catch {
        return null;
    } finally {
        if (connected) {
            // connect()成功時、チップはROMブートローダーに入ったままになる(esptool-js側で
            // 自動的には抜けない)。ここでリセットし直さないと、以後そのボードが通常のアプリ
            // ファームウェアを実行しない(=シリアルモニター等が無応答になる)状態が残ってしまう。
            // BlueUpload成功時と同じ「EN=LOW→GPIO0=HIGHで再起動」の手順(useBoardWebSerialApiUpload.ts)。
            try {
                await transport.setDTR(false);
                await transport.setRTS(true);
                await new Promise((resolve) => setTimeout(resolve, 100));
                await transport.setRTS(false);
                // リセット直後のチップはまだ起動が安定しておらず、この直後に別の呼び出し元が
                // 同じポートへROM再突入を試みると、シリアルの同期ハンドシェイクが確実に失敗する
                // (実機で100%再現を確認済み)。安定するまで少し待ってから返す。
                await new Promise((resolve) => setTimeout(resolve, 800));
            } catch {
                // リセットに失敗しても検出結果自体は有効なので握りつぶす
            }
        }
        await forceCloseTransport(port, transport);
    }
}

export async function isPhysicallyIotBoard(port: SerialPort): Promise<boolean> {
    return (await identifyIotBoard(port)) !== null;
}

// chip_idはDB照会のキーとして使うため、identifyIotBoard(「選んだのがオレンジなのに実機がIoT系か」を
// 素早く判定するだけの、多少失敗してもよい用途)の400ms/1回リトライ設定は使わない。
// タイムアウトはesptool-js本家のデフォルト(3000ms)のまま明示しておく。
const CHIP_ID_PROBE_TIMEOUT_MS = 3000;
// リトライ回数はesptool-js本家のconnect()のデフォルト値(7回、50ms/550msの2種類のリセット遅延を
// 交互に試す設計)にそのまま従う。以前ここを根拠なく3回に削り、それによる失敗率上昇を「毎回
// ポートをclose/reopenして外側からリトライする」という本家がやっていない荒い方法で誤魔化して
// いたが、それ自体がさらなる不安定要因(ポート再オープンのOSレベルの遅延)になっていた。
// 本家の値を尊重してそのまま渡すのが正しい対応。

// 導出根拠(実機の誤った戻り値から逆算・確定): readMac()が返すmacAddress文字列は、
// ESP.getEfuseMac()の生値を素直にhex印字した場合とはバイト順が逆になっている。
// macAddress文字列の先頭3バイトではなく、末尾3バイトを逆順にせずそのまま結合すると
// scmUtils.getChipId(ESP.getEfuseMac())と一致する。
export interface IotBoardChipIdProbeResult {
    macAddress: string;
    chipId: number;
}

function macAddressToChipId(macAddress: string): number | null {
    const bytes = macAddress.split(':').map((b) => parseInt(b, 16));
    if (bytes.length !== 6 || bytes.some((b) => Number.isNaN(b))) return null;
    const [, , , m3, m4, m5] = bytes;
    return (m3 << 16) | (m4 << 8) | m5;
}

export async function readChipIdViaRom(port: SerialPort): Promise<IotBoardChipIdProbeResult | null> {
    await port.close().catch(() => {});
    const transport = new Transport(port, true);
    const esploader = new ESPLoader({
        transport,
        baudrate: 115200,
        romBaudrate: 115200,
        terminal: {
            clean() {},
            writeLine() {},
            write() {},
        },
    });
    esploader.DEFAULT_TIMEOUT = CHIP_ID_PROBE_TIMEOUT_MS;
    let connected = false;
    try {
        // attemptsを明示的に渡さず、esptool-js本家のデフォルト(7回)に従う。
        await esploader.connect('default_reset', undefined, true);
        connected = true;
        if (!esploader.chip) return null;
        const macAddress = await esploader.chip.readMac(esploader);
        const chipId = macAddressToChipId(macAddress);
        if (chipId === null) return null;
        return { macAddress, chipId };
    } catch {
        return null;
    } finally {
        if (connected) {
            // connect()成功時、チップはROMブートローダーに入ったままになる(esptool-js側で
            // 自動的には抜けない)。ここでリセットし直さないと、以後そのボードが通常のアプリ
            // ファームウェアを実行しない(=シリアルモニター等が無応答になる)状態が残ってしまう。
            try {
                await transport.setDTR(false);
                await transport.setRTS(true);
                await new Promise((resolve) => setTimeout(resolve, 100));
                await transport.setRTS(false);
                // リセット直後のチップはまだ起動が安定しておらず、この直後に(呼び出し元の
                // 即時リトライ含め)別のROM再突入を試みると、シリアルの同期ハンドシェイクが
                // 確実に失敗する(実機で100%再現を確認済み、identifyIotBoardと同じ現象)。
                // 安定するまで少し待ってから返す。
                await new Promise((resolve) => setTimeout(resolve, 800));
            } catch {
                // リセットに失敗しても検出結果自体は有効なので握りつぶす
            }
        }
        await forceCloseTransport(port, transport);
    }
}
